/**
 * The user's UI documents (docs/audits/PILOT-QUOTES-SHELL.md §5, S4b; S4a's
 * /api/me/ui-preferences/:module, migration 340).
 *
 * THE PREFERENCE FOLLOWS THE LOGIN, NEVER THE DEVICE (§5.4, DECISION 7)
 * - Loaded once after login: one GET per module ('shell', 'quotes', since C4b 'customers', and
 *   since O4 'orders').
 * - Held in memory and in a per-user cache key, crm.ui.prefs.<userId>.<module>.v1, so the
 *   next login on this device paints the user's own choices before the GET answers.
 * - Logout drops the in-memory documents; the next user reads their own.
 * - Nothing is ever imported from a device key. The old ones (crm.ui.sidebar.v1,
 *   quoteListViewMode, S3's crm.quotes.recent.v1.*) are deleted on first run, so a device
 *   cannot pass one user's choice to the next.
 *
 * READ: a GET that fails (a 5xx, offline) falls back silently to the defaults and retries later
 * (FIRST_RETRY_MS, doubling to MAX_RETRY_MS). A 404 (O4) is a backend that has no such document
 * — older than the module; the CRM and the backend release separately — so it is the defaults,
 * silently and for the session: no retry loop, and nothing is written there (a change still
 * applies on screen). Every document read is checked against the §5.2 shapes
 * (preferenceShapes.js).
 *
 * WRITE: update(mutator) applies the change at once and queues the mutator. Writes are
 * debounced (WRITE_DEBOUNCE_MS, §5.3) and send the WHOLE document with the version last
 * read. On a 409 the server's current document (error.details) becomes the base, the queued
 * changes are re-applied on top, and the PUT goes again with details.version — two tabs or
 * two devices on one login never clobber each other. A change made before the first read
 * (or while it is failing) waits for the read, then is applied on top of it.
 *
 * NOTHING CHANGED, NOTHING SENT (C4b): a read never writes (not even when the sanitiser trimmed
 * the stored document); an update whose result equals the document is dropped; and a write
 * whose document equals the one last read (a change undone inside the debounce) is not sent.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useAuth } from './AuthContext';
import { authFetch } from '../services/authFetch';
import { readGuarded, writeGuarded } from '../utils/guardedStorage';
import { sameDoc, sanitizeDoc } from './preferenceShapes';

export const MODULES = ['shell', 'quotes', 'customers', 'orders'];
export const WRITE_DEBOUNCE_MS = 1000;
export const FIRST_RETRY_MS = 30000;
export const MAX_RETRY_MS = 5 * 60 * 1000;
const MAX_CONFLICT_RETRIES = 3;

export const prefsCacheKey = (userId, module) => `crm.ui.prefs.${userId}.${module}.v1`;

/** DECISION 7: the device keys S4 replaces. Never read; removed on first run. */
export const RETIRED_DEVICE_KEYS = ['crm.ui.sidebar.v1', 'quoteListViewMode'];
const RETIRED_DEVICE_PREFIXES = ['crm.quotes.recent.v1.'];

export function removeRetiredDeviceKeys() {
  try {
    for (const key of RETIRED_DEVICE_KEYS) localStorage.removeItem(key);
    const doomed = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && RETIRED_DEVICE_PREFIXES.some((p) => key.startsWith(p))) doomed.push(key);
    }
    doomed.forEach((key) => localStorage.removeItem(key));
  } catch {
    /* storage unavailable: nothing to remove */
  }
}

const isCacheEntry = (v) => v !== null && typeof v === 'object' && Number.isInteger(v.version) && v.version >= 0;
const url = (module) => `/api/me/ui-preferences/${module}`;

async function readBody(res) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * One user's documents. Framework-free, so the provider can swap it on login / logout and
 * readers subscribe with useSyncExternalStore.
 */
export function createPreferencesStore(userId) {
  const listeners = new Set();
  const slots = {};
  let disposed = false;

  for (const module of MODULES) {
    const cached = readGuarded(prefsCacheKey(userId, module), isCacheEntry, null);
    slots[module] = {
      base: cached ? sanitizeDoc(module, cached.doc) : {}, // the server's document, as last read
      version: cached ? cached.version : 0,
      pending: [], // mutators not yet acknowledged by the server
      loaded: false,
      inflight: false,
      again: false,
      conflicts: 0,
      writeTimer: null,
      retryTimer: null,
      retryMs: FIRST_RETRY_MS,
      snapshot: null
    };
    slots[module].snapshot = snapshotOf(module, 'loading');
  }

  function snapshotOf(module, status) {
    const s = slots[module];
    let doc = s.base;
    for (const fn of s.pending) doc = sanitizeDoc(module, fn(doc));
    return { doc, version: s.version, status };
  }

  function publish(module, status = slots[module].snapshot.status) {
    const s = slots[module];
    s.snapshot = snapshotOf(module, status);
    writeGuarded(prefsCacheKey(userId, module), { doc: s.snapshot.doc, version: s.version });
    listeners.forEach((fn) => fn());
  }

  function scheduleRetry(module, fn) {
    const s = slots[module];
    clearTimeout(s.retryTimer);
    s.retryTimer = setTimeout(() => { if (!disposed) fn(); }, s.retryMs);
    s.retryMs = Math.min(s.retryMs * 2, MAX_RETRY_MS);
  }

  function scheduleWrite(module, delay = WRITE_DEBOUNCE_MS) {
    const s = slots[module];
    if (!s.loaded || !s.pending.length) return;
    clearTimeout(s.writeTimer);
    s.writeTimer = setTimeout(() => { s.writeTimer = null; write(module); }, delay);
  }

  async function load(module) {
    const s = slots[module];
    let res;
    try {
      res = await authFetch(url(module));
    } catch {
      res = null;
    }
    if (disposed) return;
    const body = res && res.ok ? await readBody(res) : null;
    if (disposed) return;
    const data = body && body.data;
    if (!data || !Number.isInteger(data.version)) {
      publish(module, 'fallback');
      // A 404 will answer the same until the backend is upgraded: no retry loop.
      if (!(res && res.status === 404)) scheduleRetry(module, () => load(module));
      return;
    }
    s.base = sanitizeDoc(module, data.doc);
    s.version = data.version;
    s.loaded = true;
    s.retryMs = FIRST_RETRY_MS;
    publish(module, 'ready');
    scheduleWrite(module); // changes made before the read go on top of it
  }

  async function write(module) {
    const s = slots[module];
    if (!s.loaded || !s.pending.length) return;
    if (!s.inflight && sameDoc(s.snapshot.doc, s.base)) {
      // The changes cancel out against the document last read: nothing to send.
      s.pending = [];
      return;
    }
    if (s.inflight) {
      s.again = true;
      return;
    }
    s.inflight = true;
    const sent = s.pending.length;
    const doc = s.snapshot.doc;
    let res;
    try {
      res = await authFetch(url(module), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ doc, version: s.version })
      });
    } catch {
      res = null;
    }
    const body = res ? await readBody(res) : null;
    s.inflight = false;
    if (disposed) return;

    if (res && res.ok && body && body.data && Number.isInteger(body.data.version)) {
      s.base = sanitizeDoc(module, body.data.doc);
      s.version = body.data.version;
      s.pending = s.pending.slice(sent);
      s.conflicts = 0;
      s.retryMs = FIRST_RETRY_MS;
      publish(module);
    } else if (res && res.status === 409 && body && body.error && body.error.details
      && Number.isInteger(body.error.details.version) && s.conflicts < MAX_CONFLICT_RETRIES) {
      // Someone else wrote first: their document is the base, ours goes on top of it.
      s.base = sanitizeDoc(module, body.error.details.doc);
      s.version = body.error.details.version;
      s.conflicts += 1;
      publish(module);
      s.again = true;
    } else {
      // Offline, a 5xx, a backend without S4a, or conflicts that will not settle: keep the
      // change here and try again later.
      s.conflicts = 0;
      scheduleRetry(module, () => write(module));
      return;
    }
    if (s.again || s.pending.length) {
      s.again = false;
      if (s.pending.length) write(module);
    }
  }

  return {
    userId,
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    get(module) {
      return slots[module].snapshot;
    },
    start() {
      MODULES.forEach((m) => load(m));
    },
    update(module, mutator) {
      const s = slots[module];
      const before = s.snapshot.doc;
      s.pending.push(mutator);
      if (sameDoc(snapshotOf(module, s.snapshot.status).doc, before)) {
        s.pending.pop(); // it changes nothing: no publish, no write
        return;
      }
      publish(module);
      scheduleWrite(module);
    },
    /** Send what is waiting now (before the token goes at logout). */
    flush() {
      for (const module of MODULES) {
        const s = slots[module];
        if (s.writeTimer) {
          clearTimeout(s.writeTimer);
          s.writeTimer = null;
          write(module);
        }
      }
    },
    dispose() {
      disposed = true;
      for (const module of MODULES) {
        clearTimeout(slots[module].writeTimer);
        clearTimeout(slots[module].retryTimer);
      }
      listeners.clear();
    }
  };
}

const PreferencesContext = createContext(null);

/** Inside AuthProvider: swaps the store whenever the logged-in user changes. */
export function PreferencesProvider({ children }) {
  const { user } = useAuth() || {};
  const userId = user && user.id ? user.id : null;

  // DECISION 7, once per page load: the device keys go before anything could read them.
  const cleaned = useRef(false);
  if (!cleaned.current) {
    cleaned.current = true;
    removeRetiredDeviceKeys();
  }

  const store = useMemo(() => (userId ? createPreferencesStore(userId) : null), [userId]);

  useEffect(() => {
    if (!store) return undefined;
    store.start();
    // AuthContext announces a logout while the token is still there: send what is waiting.
    const onBeforeLogout = () => store.flush();
    window.addEventListener('crm:before-logout', onBeforeLogout);
    return () => {
      window.removeEventListener('crm:before-logout', onBeforeLogout);
      store.flush();
      store.dispose();
    };
  }, [store]);

  return <PreferencesContext.Provider value={store}>{children}</PreferencesContext.Provider>;
}

const NO_STORE = { subscribe: () => () => {}, get: () => null };

/**
 * usePreferences(module) -> { doc, version, status, update(mutator), userId }
 *   doc       the user's document, checked against §5.2 ({} = all defaults)
 *   status    'loading' | 'ready' | 'fallback' (the read failed; defaults; retrying) |
 *             'none' (no login: defaults, kept in this component only)
 *   update    update((doc) => nextDoc): applied at once, written debounced
 */
export function usePreferences(module) {
  const store = useContext(PreferencesContext);
  const source = store || NO_STORE;
  const snapshot = useSyncExternalStore(source.subscribe, () => source.get(module), () => source.get(module));

  // No login (or no provider): the defaults, and changes kept in this component only.
  const [localDoc, setLocalDoc] = useState({});
  const localUpdate = useCallback((mutator) => {
    setLocalDoc((d) => sanitizeDoc(module, mutator(d)));
  }, [module]);
  const storeUpdate = useCallback((mutator) => store && store.update(module, mutator), [store, module]);

  if (!store || !snapshot) {
    return { doc: localDoc, version: 0, status: 'none', update: localUpdate, userId: null };
  }
  return { ...snapshot, update: storeUpdate, userId: store.userId };
}
